import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useParams, useHistory, Link } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Language as WebsiteIcon,
  AccountBalance as EINIcon,
  AttachMoney as PlanIcon,
  BugReport as BugReportIcon,
  AccountTree as SponsorIcon,
  AccountCircle as ProfileIcon,
  Close as CloseIcon,
} from '@material-ui/icons'
import dayjs from 'dayjs'
import { colors } from '../../../../Themes/Colors'
import { NonprofitOrg } from '../../../../Generated/graphql-types'
import GET_NONPROFIT_BY_ID from '../../../../Apollo/Queries/GetNonprofitById'
import { NonprofitProfile } from '../NonprofitProfile/NonprofitProfile'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  StatusChip,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  SectionTitle,
  InfoCard,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ScrollablePanel,
  EmptyState,
  ActionBar,
  LoadingContainer,
  ErrorContainer,
} from './NonprofitDetail.styled'

interface RouteParams {
  id: string
}

// Extended type to include all the fields we need
interface ExtendedNonprofitOrg extends NonprofitOrg {
  disabled?: boolean
  formationPlan?: string | null
  platformPlan?: string | null
  ein?: string | null
  legalName?: string | null
  websiteUrl?: string | null
  isFiscallySponsored?: boolean
  isTest?: boolean
  state?: string | null
  yearEstablished?: number | null
  createdAt?: string | null
  updatedAt?: string | null
  disabledAt?: string | null
  funders?: Array<{ id: number; name?: string | null }> | null
  cohorts?: Array<{ id: number; name?: string | null }> | null
  users?: Array<{
    id: number
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    role?: string | null
  }> | null
  hasProfile?: boolean
}

export const NonprofitDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const nonprofitId = parseInt(id, 10)
  const [profileModalOpen, setProfileModalOpen] = useState(false)

  const { data, loading, error } = useQuery<{
    getNonprofitOrg: ExtendedNonprofitOrg
  }>(GET_NONPROFIT_BY_ID, {
    variables: { id: nonprofitId },
    skip: !nonprofitId,
  })

  const nonprofit = data?.getNonprofitOrg

  const handleBack = () => {
    history.push('/internal/self-serve/nonprofit-admin')
  }

  const navigateToFunders = () => {
    history.push(
      `/internal/self-serve/funder-admin?nonprofitName=${encodeURIComponent(
        nonprofit?.name || ''
      )}`
    )
  }

  const navigateToCohorts = () => {
    history.push(
      `/internal/self-serve/cohort-admin?nonprofitName=${encodeURIComponent(
        nonprofit?.name || ''
      )}`
    )
  }

  const navigateToUsers = () => {
    // Navigate to user admin with nonprofit filter
    history.push(
      `/internal/self-serve/user-admin?nonprofitName=${encodeURIComponent(
        nonprofit?.name || ''
      )}`
    )
  }

  const handleOpenProfile = () => {
    setProfileModalOpen(true)
  }

  const handleCloseProfile = () => {
    setProfileModalOpen(false)
  }

  if (error) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofits
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Error Loading Nonprofit
          </Typography>
          <Typography>{error.message}</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (loading) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofits
        </BackButton>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (!nonprofit) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofits
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Nonprofit Not Found
          </Typography>
          <Typography>Nonprofit with ID {nonprofitId} not found</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  const formatDate = (date: string | Date | null | undefined) =>
    date ? dayjs(date).format('MMM D, YYYY h:mm A') : 'N/A'

  const getPlan = () => {
    if (nonprofit.formationPlan) return nonprofit.formationPlan
    if (nonprofit.platformPlan) return nonprofit.platformPlan
    return 'N/A'
  }

  return (
    <PageContainer maxWidth='lg'>
      <HeaderSection>
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant='text'
        >
          Back to Nonprofits
        </BackButton>

        <TitleSection>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            width='100%'
          >
            <Box>
              <Box display='flex' alignItems='center'>
                <Title>{nonprofit.name || 'Unnamed Nonprofit'}</Title>
                <StatusChip
                  label={nonprofit.disabled ? 'Disabled' : 'Active'}
                  status={nonprofit.disabled ? 'disabled' : 'active'}
                  size='small'
                />
                {nonprofit.isTest && (
                  <StatusChip
                    label='Test'
                    status='test'
                    size='small'
                    icon={<BugReportIcon />}
                  />
                )}
                {nonprofit.isFiscallySponsored && (
                  <StatusChip
                    label='Fiscally Sponsored'
                    status='sponsored'
                    size='small'
                    icon={<SponsorIcon />}
                  />
                )}
              </Box>
              <Subtitle>
                Nonprofit ID: {nonprofit.id}
                {nonprofit.ein && ` • EIN: ${nonprofit.ein}`}
              </Subtitle>
            </Box>
            <Box>
              <Tooltip title='View Nonprofit Profile'>
                <Button
                  variant='contained'
                  size='small'
                  startIcon={<ProfileIcon />}
                  onClick={handleOpenProfile}
                  style={{
                    backgroundColor: colors.resiliaBlue,
                    color: 'white',
                    textTransform: 'none',
                  }}
                >
                  View Profile
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </TitleSection>
      </HeaderSection>

      {/* Statistics Cards */}
      <Grid container spacing={3} marginBottom={4}>
        <Grid item xs={12} sm={3}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <BusinessIcon fontSize='large' />
              </StatIcon>
              <StatValue>{nonprofit.funders?.length || 0}</StatValue>
              <StatLabel>Funders</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={3}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <SchoolIcon fontSize='large' />
              </StatIcon>
              <StatValue>{nonprofit.cohorts?.length || 0}</StatValue>
              <StatLabel>Cohorts</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={3}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <GroupIcon fontSize='large' />
              </StatIcon>
              <StatValue>{nonprofit.users?.length || 0}</StatValue>
              <StatLabel>Team Members</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={3}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <ProfileIcon fontSize='large' />
              </StatIcon>
              <StatValue>{nonprofit.hasProfile ? 'Yes' : 'No'}</StatValue>
              <StatLabel>Has Profile</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Basic Information */}
      <InfoCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Basic Information
          </SectionTitle>
          <InfoGrid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Legal Name</InfoLabel>
                <InfoValue>
                  {nonprofit.legalName || nonprofit.name || 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  {nonprofit.disabled ? (
                    <>
                      <CancelIcon fontSize='small' color='error' />
                      Disabled
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon
                        fontSize='small'
                        style={{ color: colors.darkGreen }}
                      />
                      Active
                    </>
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Plan</InfoLabel>
                <InfoValue>
                  <PlanIcon fontSize='small' />
                  {getPlan()}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>EIN</InfoLabel>
                <InfoValue>
                  <EINIcon fontSize='small' />
                  {nonprofit.ein || 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Website</InfoLabel>
                <InfoValue>
                  <WebsiteIcon fontSize='small' />
                  {nonprofit.websiteUrl ? (
                    <a
                      href={nonprofit.websiteUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {nonprofit.websiteUrl}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>State</InfoLabel>
                <InfoValue>{nonprofit.state || 'N/A'}</InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Year Established</InfoLabel>
                <InfoValue>{nonprofit.yearEstablished || 'N/A'}</InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Environment</InfoLabel>
                <InfoValue>
                  {nonprofit.isTest ? (
                    <>
                      <BugReportIcon fontSize='small' color='action' />
                      Test Environment
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon
                        fontSize='small'
                        style={{ color: colors.resiliaBlue }}
                      />
                      Production
                    </>
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Fiscally Sponsored</InfoLabel>
                <InfoValue>
                  {nonprofit.isFiscallySponsored ? (
                    <>
                      <CheckCircleIcon
                        fontSize='small'
                        style={{ color: colors.darkGreen }}
                      />
                      Yes
                    </>
                  ) : (
                    'No'
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(nonprofit.createdAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(nonprofit.updatedAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Disabled At</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {nonprofit.disabledAt
                    ? formatDate(nonprofit.disabledAt)
                    : 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
          </InfoGrid>
        </CardContent>
      </InfoCard>

      {/* Mission Statement */}
      {nonprofit.missionStatement && (
        <InfoCard>
          <CardContent>
            <SectionTitle>
              <InfoIcon />
              Mission Statement
            </SectionTitle>
            <Typography variant='body1'>
              {nonprofit.missionStatement}
            </Typography>
          </CardContent>
        </InfoCard>
      )}

      {/* Three column layout for related entities */}
      <Grid container spacing={3}>
        {/* Funders Panel */}
        <Grid item xs={12} md={4}>
          <SectionTitle>
            <BusinessIcon />
            Associated Funders ({nonprofit.funders?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {nonprofit.funders && nonprofit.funders.length > 0 ? (
              <List>
                {nonprofit.funders.map((funder) => (
                  <ListItem key={funder?.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.resiliaBlue }}>
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        funder?.name ? (
                          <Link
                            to={`/internal/self-serve/funder-admin/${funder.id}`}
                          >
                            {funder.name}
                          </Link>
                        ) : (
                          'Unknown'
                        )
                      }
                      secondary={`ID: ${funder?.id}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>
                <BusinessIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>No funders associated</Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>

        {/* Cohorts Panel */}
        <Grid item xs={12} md={4}>
          <SectionTitle>
            <SchoolIcon />
            Associated Cohorts ({nonprofit.cohorts?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {nonprofit.cohorts && nonprofit.cohorts.length > 0 ? (
              <List>
                {nonprofit.cohorts.map((cohort) => (
                  <ListItem key={cohort?.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.resiliaPurple }}>
                        <SchoolIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        cohort?.name ? (
                          <Link
                            to={`/internal/self-serve/cohort-admin/${cohort.id}`}
                          >
                            {cohort.name}
                          </Link>
                        ) : (
                          'Unknown'
                        )
                      }
                      secondary={`ID: ${cohort?.id}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>
                <SchoolIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>No cohorts associated</Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>

        {/* Team Members Panel */}
        <Grid item xs={12} md={4}>
          <SectionTitle>
            <GroupIcon />
            Team Members ({nonprofit.users?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {nonprofit.users && nonprofit.users.length > 0 ? (
              <List>
                {nonprofit.users.map((user) => (
                  <ListItem key={user?.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.paletteOrange }}>
                        {user?.firstName?.[0] || user?.email?.[0] || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        user ? (
                          <Link
                            to={`/internal/self-serve/user-admin/${user.id}`}
                          >
                            {`${user.firstName || ''} ${
                              user.lastName || ''
                            }`.trim() || 'Unnamed User'}
                          </Link>
                        ) : (
                          'Unknown'
                        )
                      }
                      secondary={
                        <>
                          {user?.email}
                          {user?.role && ` • ${user.role}`}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>
                <GroupIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>No team members</Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <ActionBar>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<BusinessIcon />}
          onClick={navigateToFunders}
          disabled={!nonprofit.funders || nonprofit.funders.length === 0}
        >
          View All Funders
        </Button>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<SchoolIcon />}
          onClick={navigateToCohorts}
          disabled={!nonprofit.cohorts || nonprofit.cohorts.length === 0}
        >
          View All Cohorts
        </Button>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<GroupIcon />}
          onClick={navigateToUsers}
          disabled={!nonprofit.users || nonprofit.users.length === 0}
        >
          View All Users
        </Button>
      </ActionBar>

      {/* Profile Modal */}
      <Dialog
        open={profileModalOpen}
        onClose={handleCloseProfile}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          style: {
            height: '90vh',
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          padding={2}
          borderBottom={`1px solid ${colors.lightGrey}`}
          bgcolor='white'
        >
          <Typography variant='h6' style={{ color: colors.greyBlue }}>
            Nonprofit Profile
          </Typography>
          <IconButton onClick={handleCloseProfile} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent style={{ padding: 0, overflow: 'auto' }}>
          <NonprofitProfile />
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
